import { useMemo, useState } from 'react';
import type { Dimension, LevelDescriptor } from '../../shared/types.js';
import { LEVELS } from '../../shared/levels.js';
import { api } from '../api/client.js';

interface Props {
  dimensions: Dimension[];
  descriptors: LevelDescriptor[];
  config: Record<string, string>;
  onChanged: () => void;
}

export function FrameworkEditor({ dimensions, descriptors, config, onChanged }: Props) {
  const byCell = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of descriptors) m.set(`${d.dimension_id}|${d.level}`, d.descriptor);
    return m;
  }, [descriptors]);

  const [selectedDim, setSelectedDim] = useState<string>(() => dimensions[0]?.id ?? '');
  const [editing, setEditing] = useState<{ dim: string; level: string } | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function selectDim(id: string) {
    setSelectedDim(id);
    setEditing(null);
    setError(null);
  }

  function open(dim: string, level: string) {
    setEditing({ dim, level });
    setDraft(byCell.get(`${dim}|${level}`) ?? '');
    setError(null);
  }

  async function save() {
    if (!editing) return;
    try {
      await api.upsertLevelDescriptor(editing.dim, editing.level, { descriptor: draft.trim() });
      setError(null);
      setEditing(null);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  }

  async function saveConfig(key: 'assigned_level' | 'target_level', value: string) {
    try {
      await api.updateConfig({ [key]: value });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Config save failed');
    }
  }

  const active = dimensions.find((d) => d.id === selectedDim) ?? dimensions[0];

  return (
    <div className="framework">
      {error && <p className="error">{error}</p>}
      <div className="report-controls">
        <label className="filter-date">
          assigned
          <select value={config.assigned_level ?? ''} onChange={(e) => saveConfig('assigned_level', e.target.value)}>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-date">
          target
          <select value={config.target_level ?? ''} onChange={(e) => saveConfig('target_level', e.target.value)}>
            {LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="fw-layout">
        <nav className="fw-dimlist">
          {dimensions.map((d) => (
            <button
              key={d.id}
              type="button"
              className={d.id === active?.id ? 'fw-dimitem on' : 'fw-dimitem'}
              onClick={() => selectDim(d.id)}
            >
              {d.name}
            </button>
          ))}
        </nav>

        <div className="fw-levels">
          {active && <h3 className="fw-active-name">{active.name}</h3>}
          {active &&
            LEVELS.map((level) => {
              const key = `${active.id}|${level}`;
              const isEditing = editing?.dim === active.id && editing?.level === level;
              const tag =
                config.assigned_level === level
                  ? 'assigned'
                  : config.target_level === level
                    ? 'target'
                    : null;
              return (
                <div key={level} className="fw-level">
                  <div className="fw-level-head">
                    <span className="fw-level-tag">
                      {level}
                      {tag && <span className={`fw-tag fw-tag-${tag}`}>{tag}</span>}
                    </span>
                    {!isEditing && (
                      <button type="button" className="ghost small" onClick={() => open(active.id, level)}>
                        Edit
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="fw-edit">
                      <textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} />
                      <div className="detail-actions">
                        <button type="button" onClick={save} disabled={draft.trim().length === 0}>
                          Save
                        </button>
                        <button type="button" className="ghost" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="fw-level-text">{byCell.get(key) ?? '—'}</p>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
